import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { X } from 'react-feather'
import { useKline } from '../../state/kline/hooks'
import { getTokenPriceData, fetchAllowedTokenList } from '../../services/tokenListApi'


const ModalContainer = styled.div`
  width: 100%;
  height: 600px;
  background: ${({ theme }) => theme.bg1};
  border-radius: 20px 20px 0px 0px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  bottom: 0;
  align-self: flex-end;
  
  @media (max-width: 768px) {
    height: 80vh;
    max-height: 600px;
    border-radius: 20px 20px 0px 0px;
  }
`

const Header = styled.div`
  padding: 5px 24px 4px;
  background: ${({ theme }) => theme.bg1};
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const TokenPair = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const PairName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text1};
  display: flex;
  align-items: center;
  gap: 8px;
`

const PriceSection = styled.div`
  margin-bottom: 10px;
`

const CurrentPrice = styled.div<{ positive?: boolean }>`
  font-size: 30px;
  font-weight: 700;
  color: ${({ theme, positive }) =>
    positive === true ? theme.green1 :
      positive === false ? theme.red1 :
        theme.text1
  };
  margin-bottom: 8px;
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text2};
  margin-bottom: 2px;
`

const StatValue = styled.div<{ positive?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme, positive }) =>
    positive === true ? theme.green1 :
      positive === false ? theme.red1 :
        theme.text1
  };
`

const ChartContainer = styled.div`
  flex: 1;
  position: relative;
  padding: 0px;
  display: flex;
  flex-direction: column;
`

const ChartArea = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: stretch;
  overflow: hidden;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  margin-left: auto;
  
  &:hover {
    background: ${({ theme }) => theme.bg3};
  }
`

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
`

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.bg1};
  z-index: 10;
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.bg3};
  border-top: 3px solid ${({ theme }) => theme.primary1};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`

const LoadingText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text2};
  font-weight: 500;
`
interface KlineModalProps {
  onClose: () => void
}

export default function KlineModal({ onClose }: KlineModalProps) {
  const { currentTradingPair } = useKline()

  const [priceData, setPriceData] = useState<{
    currentPrice: string
    change24h: string
    high24h: string
    low24h: string
    isPositive: boolean
    tokenName0: string
    tokenName1: string
  } | null>(null)

  const [isIframeLoading, setIsIframeLoading] = useState(true)

  useEffect(() => {
    const address = currentTradingPair?.token0.key
    let cancelled = false
    let running = false

    if (!address) {
      setPriceData(null)
      return
    }

    const update = async () => {
      if (running) return
      running = true
      try {
        // 拉取最新的token列表与价格数据，刷新本地缓存
        await fetchAllowedTokenList()
      } catch (e) {
        // 忽略网络错误，继续从缓存读取
      } finally {
        const data = getTokenPriceData(address)
        if (!cancelled) setPriceData(data)
        running = false
      }
    }

    // 立即拉取一次
    update()
    // 每5秒轮询一次
    const id = setInterval(update, 5000)

    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [currentTradingPair])

  // 当交易对改变时，重置 iframe 加载状态
  useEffect(() => {
    setIsIframeLoading(true)
  }, [currentTradingPair])

  const handleIframeLoad = () => {
    setIsIframeLoading(false)
  }

  return (
    <ModalContainer>
      <Header>
        <TokenPair>
          {currentTradingPair?.token0.logo && <img alt={`${currentTradingPair.token0.symbol} logo`} src={currentTradingPair.token0.logo} style={{ width: '25px'}} />}
          <PairName>
            {priceData?.tokenName1?.toUpperCase() ?? ''} / {priceData?.tokenName0?.toUpperCase() ?? ''}
          </PairName>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </TokenPair>

        <PriceSection>
          <CurrentPrice positive={priceData?.isPositive}>{priceData?.currentPrice ?? '--'}</CurrentPrice>
          <StatsRow>
            <StatItem>
              <StatLabel>24h Change</StatLabel>
              <StatValue positive={priceData?.isPositive}>
                {priceData ? `${priceData.isPositive ? '+' : '-'}${priceData.change24h}%` : '--'}
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>24h High</StatLabel>
              <StatValue>{priceData?.high24h ?? '--'}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>24h Low</StatLabel>
              <StatValue>{priceData?.low24h ?? '--'}</StatValue>
            </StatItem>
          </StatsRow>
        </PriceSection>
      </Header>

      <ChartContainer>
        <ChartArea>
          {isIframeLoading && (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>正在加载图表...</LoadingText>
            </LoadingContainer>
          )}
          <Iframe 
            src={`${process.env.REACT_APP_KCHART_URL}/?key=${currentTradingPair?.token0.key}&embed=1`}
            title={`Kline Chart for ${currentTradingPair?.token0.symbol}/WK`}
            onLoad={handleIframeLoad}
          />
        </ChartArea>
      </ChartContainer>
    </ModalContainer>
  )
}